import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, ParamMap, Params } from '@angular/router';
import { QarepaEnumInformation } from '@qarepa/common';
import { QueryParamSelectEnumFilterForm } from './query-param-select-enum-filter.form';

@Component({
  selector: 'qaroni-query-param-select-enum-filter',
  templateUrl: './query-param-select-enum-filter.component.html',
  styleUrl: './query-param-select-enum-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QueryParamSelectEnumFilterComponent<T>
  extends QueryParamSelectEnumFilterForm
  implements OnInit
{
  readonly label = input<string>('');
  readonly firstOption = input<string>($localize`Todos`);
  readonly firstOptionValue = input<string>('');
  readonly enumInformation =
    input.required<QarepaEnumInformation<PropertyKey> | null>();
  readonly enumArray = input.required<T[]>();
  readonly queryParamName = input.required<string>();
  readonly multiple = input<boolean>(false);

  readonly queryParamsChange = output<Params>();

  public valueKey: keyof T = 'key' as keyof T;
  public textKey: keyof T = 'value' as keyof T;
  private lastSelection: unknown[] = [];

  private route = inject(ActivatedRoute);
  private queryParamMap$ = this.route.queryParamMap;

  ngOnInit(): void {
    if (!this.hasQueryParamName) {
      return;
    }

    this.subs.add(this.queryParamMap$.subscribe(this.getQueryParamMap));
  }

  get hasLabel(): boolean {
    return this.label().trim().length > 0;
  }

  get hasQueryParamName(): boolean {
    return this.queryParamName().trim().length > 0;
  }

  get options(): T[] {
    return this.enumArray()
      .filter(
        value =>
          value !== null &&
          value !== undefined &&
          Object.prototype.hasOwnProperty.call(
            this.enumInformation(),
            value as PropertyKey
          )
      )
      .map(value => ({
        key: value,
        value: this.enumInformation()[value as PropertyKey].name,
      })) as T[];
  }

  public getValue(item: T) {
    return this.valueKey ? item[this.valueKey] : item;
  }

  public getText(item: T) {
    return this.textKey ? String(item[this.textKey]) : String(item);
  }

  public onchange(event: MatSelectChange): void {
    let currentSelectedValue = event.value;
    let valueToSend = '';

    const queryParamsEmit = { ...this.route.snapshot.queryParams };

    if (this.multiple()) {
      const wasAllSelected = this.lastSelection.includes(
        this.firstOptionValue()
      );
      const isAllSelected = currentSelectedValue.includes(
        this.firstOptionValue()
      );

      if (wasAllSelected && currentSelectedValue.length > 1) {
        currentSelectedValue = currentSelectedValue.filter(
          (v: unknown) => v !== this.firstOptionValue()
        );
      } else if (isAllSelected || currentSelectedValue.length === 0) {
        currentSelectedValue = [this.firstOptionValue()];
      }

      const isOnlyAllSelected =
        currentSelectedValue.length === 1 &&
        currentSelectedValue[0] === this.firstOptionValue();

      if (!isOnlyAllSelected) {
        valueToSend = currentSelectedValue.join(',');
      }

      this.paramValue.setValue(currentSelectedValue);
      this.lastSelection = currentSelectedValue;
    } else {
      if (currentSelectedValue !== this.firstOptionValue()) {
        valueToSend = currentSelectedValue;
      }
    }

    if (valueToSend !== '') {
      queryParamsEmit[this.queryParamName()] = valueToSend;
    } else {
      delete queryParamsEmit[this.queryParamName()];
    }

    this.queryParamsChange.emit(queryParamsEmit);
  }

  public getQueryParamMap = (queryParamMap: ParamMap): void => {
    const paramValues = queryParamMap.get(this.queryParamName());

    if (this.multiple()) {
      const values = paramValues
        ? paramValues.split(',')
        : [this.firstOptionValue()];
      this.paramValue.setValue(values);
      this.lastSelection = values;
      this.paramValueText = values;
    } else {
      if (paramValues) {
        this.paramValueText = paramValues;
      } else {
        this.resetQueryParamName();
      }
    }
  };
}
